<template>
  <div
    ref="el"
    class="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal"
    :class="dynamic && 'swiper-pagination-bullets-dynamic'"
    :style="strip ? { width: `${strip.width}px` } : undefined"
  >
    <button
      v-for="i in dots"
      :key="i"
      type="button"
      class="swiper-pagination-bullet"
      :class="bulletClasses(i)"
      :style="strip ? { left: `${strip.left}px` } : undefined"
      :aria-label="label(i + 1)"
      :aria-current="i === active"
      @click="emit('select', i)"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Swiper's pagination bullets for utils/carousel, as buttons, with Swiper's class names so
 * every app's existing restyle still applies. `dynamic` is Swiper's `dynamicBullets` (one
 * main bullet): a five-bullet window centred on the active one, its neighbours shrunk.
 */
import '../../assets/styles/carousel-dots.css'
import { computed, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    count: number
    active: number
    dynamic?: boolean
    label?: (n: number) => string
  }>(),
  { dynamic: false, label: (n: number) => `Gå till bild ${n}` },
)
const emit = defineEmits<{ select: [index: number] }>()

const el = ref<HTMLElement>()
const dots = computed(() => Array.from({ length: props.count }, (_, i) => i))

const ACTIVE = 'swiper-pagination-bullet-active'
const bulletClasses = (i: number) => {
  if (!props.dynamic) return i === props.active ? [ACTIVE] : []
  const offset = i - props.active
  return {
    [ACTIVE]: offset === 0,
    [`${ACTIVE}-main`]: offset === 0,
    [`${ACTIVE}-prev`]: offset === -1,
    [`${ACTIVE}-prev-prev`]: offset === -2,
    [`${ACTIVE}-next`]: offset === 1,
    [`${ACTIVE}-next-next`]: offset === 2,
  }
}

// One bullet with its margins, read from the DOM because each app sizes them its own way.
const bulletSize = ref(0)
const measure = () => {
  const bullet = el.value?.querySelector<HTMLElement>('.swiper-pagination-bullet')
  if (!bullet) return
  const style = getComputedStyle(bullet)
  bulletSize.value =
    bullet.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight)
}
onMounted(measure)
watch(() => props.count, measure, { flush: 'post' })

const strip = computed(() => {
  const size = bulletSize.value
  if (!props.dynamic || !size) return undefined
  const visible = Math.min(props.count, 5)
  return { width: size * 5, left: (size * visible - size) / 2 - props.active * size }
})
</script>
